import { useCallback, useEffect, useRef, useState } from "react";
import { BellRing } from "lucide-react";
import { getPwaFlagsCache } from "@/features/pwa/lib/flags-cache";
import { isPwaFeatureOn } from "@/features/pwa/lib/pwa-flags";
import {
  browserPushSupported,
  enablePushNotifications,
  fetchPushStatus,
} from "./push";

type PushButtonState = "hidden" | "idle" | "pending" | "subscribed";

export function PushSubscriptionButton(): React.JSX.Element | null {
  const [state, setState] = useState<PushButtonState>("hidden");
  const mountedRef = useMountedRef();
  const subscribeAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Triple gate: master AND push flag AND browser feature detection.
    // Either flag off keeps the button dormant + skips the /push/status fetch.
    if (!isPwaFeatureOn(getPwaFlagsCache(), "pwa.notifications.push_enabled")) {
      return;
    }
    if (!browserPushSupported()) return;
    const controller = new AbortController();
    void fetchPushStatus(fetch, { signal: controller.signal })
      .then((status) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        setState(status.supported && !status.subscribed ? "idle" : "hidden");
      })
      .catch(() => {});
    return () => controller.abort();
  }, [mountedRef]);

  useEffect(() => {
    return () => subscribeAbortRef.current?.abort();
  }, []);

  const handleSubscribe = useCallback(() => {
    const controller = new AbortController();
    subscribeAbortRef.current = controller;
    void subscribe(setState, mountedRef, controller.signal).finally(() => {
      if (subscribeAbortRef.current === controller)
        subscribeAbortRef.current = null;
    });
  }, [mountedRef]);

  if (state === "hidden" || state === "subscribed") return null;
  return (
    <button
      type="button"
      aria-label="Ativar notificações"
      title="Ativar notificações"
      disabled={state === "pending"}
      onClick={handleSubscribe}
      className="fixed bottom-20 right-4 z-40 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-background/80 text-brand-cyan shadow-xl backdrop-blur transition hover:bg-surface disabled:opacity-50"
    >
      <BellRing className="h-5 w-5" />
    </button>
  );
}

function useMountedRef(): React.MutableRefObject<boolean> {
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  return mountedRef;
}

async function subscribe(
  setState: (state: PushButtonState) => void,
  mountedRef: React.MutableRefObject<boolean>,
  signal: AbortSignal,
): Promise<void> {
  setState("pending");
  try {
    await enablePushNotifications(undefined, { signal });
    setStateIfMounted(setState, mountedRef, signal, "subscribed");
  } catch {
    setStateIfMounted(setState, mountedRef, signal, "idle");
  }
}

function setStateIfMounted(
  setState: (state: PushButtonState) => void,
  mountedRef: React.MutableRefObject<boolean>,
  signal: AbortSignal,
  state: PushButtonState,
): void {
  if (mountedRef.current && !signal.aborted) setState(state);
}
